import React, { useState, useEffect } from 'react';
import { MessageCircle, Copy, CheckCircle, ArrowLeft, Calendar, MapPin, Clock, Users, Edit3, PartyPopper, Check, X, HelpCircle } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const EventWhatsAppMessenger = ({ event, clubName, clubId, players = [], whatsappGroupLink, onBack, onUpdateGroupLink }) => {
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [linkInput, setLinkInput] = useState(whatsappGroupLink || '');
  const [isSavingLink, setIsSavingLink] = useState(false);

  // Generate RSVP link for the event
  const getRSVPLink = () => {
    return `https://clubhudl.com/dashboard?event=${event?.id}`;
  };

  // Generate Google Maps link for venue
  const getGoogleMapsLink = (venue) => {
    if (!venue) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`;
  };

  // Get player name by ID
  const getPlayerName = (playerId) => {
    const player = players?.find(p => p.id === playerId || p.userId === playerId);
    return player?.name || 'Unknown';
  };

  // Get RSVP counts
  const getRSVPCounts = () => {
    const rsvps = event?.rsvps || {};
    return {
      yes: Object.values(rsvps).filter(r => r.status === 'yes').length,
      no: Object.values(rsvps).filter(r => r.status === 'no').length,
      maybe: Object.values(rsvps).filter(r => r.status === 'maybe').length
    };
  };

  // Format date nicely using shared helper
  const formatEventDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return formatDate(dateStr, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get event type emoji
  const getEventTypeEmoji = () => {
    switch (event?.eventType) {
      case 'social': return '🎉';
      case 'meeting': return '📋';
      case 'training': return '🏃';
      case 'fundraiser': return '💰';
      default: return '📅';
    }
  };

  // Message Templates
  const templates = {
    eventInvite: {
      name: '📨 Event Invitation',
      icon: PartyPopper,
      generate: () => {
        if (!event) return 'No event selected';

        const emoji = getEventTypeEmoji();
        const mapsLink = event.venue ? getGoogleMapsLink(event.venue) : null;
        const rsvpLink = getRSVPLink();

        return `${emoji} *${clubName} - Event Invitation*

📅 *${event.name}*

🗓️ *Date:* ${formatEventDate(event.date)}
${event.time ? `⏰ *Time:* ${event.time}${event.endTime ? ` - ${event.endTime}` : ''}` : ''}
${event.venue ? `📍 *Venue:* ${event.venue}` : ''}
${mapsLink ? `🗺️ *Directions:* ${mapsLink}` : ''}

${event.description ? `📝 ${event.description}\n` : ''}
🔔 *Please RSVP to confirm your attendance!*
👉 ${rsvpLink}

Looking forward to seeing everyone there! 🙌`;
      }
    },
    eventReminder: {
      name: '⏰ Event Reminder',
      icon: Clock,
      generate: () => {
        if (!event) return 'No event selected';

        const rsvps = event.rsvps || {};
        const attending = Object.entries(rsvps)
          .filter(([_, r]) => r.status === 'yes')
          .map(([id]) => getPlayerName(id));
        const maybe = Object.entries(rsvps)
          .filter(([_, r]) => r.status === 'maybe')
          .map(([id]) => getPlayerName(id));
        const counts = getRSVPCounts();

        const emoji = getEventTypeEmoji();
        const mapsLink = event.venue ? getGoogleMapsLink(event.venue) : null;
        const rsvpLink = getRSVPLink();

        return `${emoji} *Event Reminder - ${event.name}*

Hey team! 👋

Friendly reminder about our upcoming event:

📅 *${formatEventDate(event.date)}*
${event.time ? `⏰ ${event.time}${event.endTime ? ` - ${event.endTime}` : ''}` : ''}
${event.venue ? `📍 ${event.venue}` : ''}
${mapsLink ? `🗺️ ${mapsLink}` : ''}

✅ *Attending (${attending.length}):*
${attending.length > 0 ? attending.join(', ') : 'No responses yet'}
${maybe.length > 0 ? `\n❓ *Maybe (${maybe.length}):* ${maybe.join(', ')}` : ''}

${counts.yes + counts.no + counts.maybe < players.length ? `\n🔔 *Haven't RSVP'd yet?*\n👉 ${rsvpLink}\n` : ''}
See you there! 🙌`;
      }
    },
    eventUpdate: {
      name: '📢 Event Update',
      icon: Edit3,
      generate: () => {
        if (!event) return 'No event selected';

        const emoji = getEventTypeEmoji();
        const mapsLink = event.venue ? getGoogleMapsLink(event.venue) : null;
        const rsvpLink = getRSVPLink();

        return `${emoji} *Event Update - ${event.name}*

Hi everyone! 📢

Here's an update on our upcoming event:

📅 *Date:* ${formatEventDate(event.date)}
${event.time ? `⏰ *Time:* ${event.time}${event.endTime ? ` - ${event.endTime}` : ''}` : ''}
${event.venue ? `📍 *Venue:* ${event.venue}` : ''}
${mapsLink ? `🗺️ *Directions:* ${mapsLink}` : ''}

[Add your update here]

👉 Update your RSVP: ${rsvpLink}

Please check your RSVP and update if needed! 🙏`;
      }
    },
    custom: {
      name: '✍️ Custom Message',
      icon: Edit3,
      generate: () => ''
    }
  };

  // Auto-generate message when template changes
  useEffect(() => {
    if (selectedTemplate && selectedTemplate !== 'custom') {
      const template = templates[selectedTemplate];
      if (template) {
        setMessage(template.generate());
        setCustomMode(false);
      }
    } else if (selectedTemplate === 'custom') {
      setCustomMode(true);
      setMessage('');
    }
  }, [selectedTemplate, event]);

  // Function to copy message to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Function to send message via WhatsApp
  const sendToWhatsApp = () => {
    if (!message) return;

    navigator.clipboard.writeText(message).then(() => {
      console.log('✅ Message copied to clipboard');
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);

      if (whatsappGroupLink) {
        window.open(whatsappGroupLink, '_blank');
      } else {
        window.open('https://web.whatsapp.com/', '_blank');
      }
    }).catch(err => {
      console.error('❌ Failed to copy message:', err);
      alert('Failed to copy message. Please copy manually.');
    });
  };

  const counts = getRSVPCounts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors bg-white/60 backdrop-blur-sm px-4 py-2 rounded-lg hover:bg-white/80"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Events
          </button>

          <div className="flex items-center justify-between bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-white/50">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">Event WhatsApp Message</h2>
              <p className="text-gray-600 mt-1">Send event notifications to your club members</p>
            </div>
            <div className="bg-gradient-to-br from-green-400 to-teal-500 p-4 rounded-2xl shadow-lg">
              <PartyPopper className="text-white" size={40} />
            </div>
          </div>
        </div>

        {/* Event Info Card */}
        <div className="bg-gradient-to-r from-green-100 to-teal-100 rounded-xl border border-green-200 p-5 mb-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-lg text-green-900">{event?.name}</h3>
              <div className="mt-2 space-y-1 text-sm text-green-700">
                <div className="flex items-center gap-2">
                  <Calendar size={14} />
                  <span>{formatEventDate(event?.date)}</span>
                </div>
                {event?.time && (
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span>{event.time}{event.endTime ? ` - ${event.endTime}` : ''}</span>
                  </div>
                )}
                {event?.venue && (
                  <div className="flex items-center gap-2">
                    <MapPin size={14} />
                    <span>{event.venue}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-green-700">RSVPs</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-green-600 flex items-center gap-1">
                  <Check size={14} /> {counts.yes}
                </span>
                <span className="text-red-600 flex items-center gap-1">
                  <X size={14} /> {counts.no}
                </span>
                <span className="text-yellow-600 flex items-center gap-1">
                  <HelpCircle size={14} /> {counts.maybe}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp Group Status */}
        <div className={`border rounded-xl p-4 mb-6 ${whatsappGroupLink ? 'bg-white border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className={`flex-shrink-0 mt-0.5 ${whatsappGroupLink ? 'text-green-600' : 'text-yellow-600'}`}>
                {whatsappGroupLink ? <CheckCircle size={20} /> : '⚠️'}
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${whatsappGroupLink ? 'text-green-800' : 'text-yellow-800'}`}>
                  {whatsappGroupLink ? 'WhatsApp Group Link Active' : 'No WhatsApp Group Link Set'}
                </h3>
                {!isEditingLink && (
                  <p className={`mt-1 text-sm ${whatsappGroupLink ? 'text-green-700' : 'text-yellow-700'}`}>
                    {whatsappGroupLink
                      ? 'Your members can join the group directly using the generated links.'
                      : "Add your club's WhatsApp group link for one-click access."}
                  </p>
                )}

                {isEditingLink && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      type="url"
                      value={linkInput}
                      onChange={(e) => setLinkInput(e.target.value)}
                      placeholder="https://chat.whatsapp.com/..."
                      className="text-sm px-3 py-2 border border-gray-300 rounded-md w-64 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                    <button
                      onClick={async () => {
                        setIsSavingLink(true);
                        try {
                          await onUpdateGroupLink(linkInput);
                          setIsEditingLink(false);
                        } catch (e) {
                          alert(`Failed to save link: ${e.message || 'Unknown error'}`);
                        } finally {
                          setIsSavingLink(false);
                        }
                      }}
                      disabled={isSavingLink}
                      className="text-sm bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isSavingLink ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingLink(false);
                        setLinkInput(whatsappGroupLink || '');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 px-2"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {!isEditingLink && (
              <button
                onClick={() => {
                  setLinkInput(whatsappGroupLink || '');
                  setIsEditingLink(true);
                }}
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm ${whatsappGroupLink
                  ? 'text-green-700 bg-green-50 hover:bg-green-100 border border-green-200'
                  : 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 border border-yellow-300'
                  }`}
              >
                {whatsappGroupLink ? 'Edit Link' : 'Add Link'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Template Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-green-100 p-6 sticky top-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center">
                <MessageCircle size={20} className="mr-2 text-green-600" />
                Message Templates
              </h3>

              <div className="space-y-2">
                {Object.entries(templates).map(([key, template]) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedTemplate(key)}
                      className={`w-full flex items-center p-3 rounded-lg transition-all ${selectedTemplate === key
                        ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-md'
                        : 'bg-gray-50 text-gray-700 hover:bg-green-50 hover:text-green-700'
                        }`}
                    >
                      <Icon size={18} className="mr-3" />
                      <span className="text-sm font-medium">{template.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Message Editor and Preview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Message Editor */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Message Editor</h3>
                {selectedTemplate && !customMode && (
                  <button
                    onClick={() => setCustomMode(true)}
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
                  >
                    <Edit3 size={14} className="mr-1" />
                    Edit Message
                  </button>
                )}
              </div>

              {!selectedTemplate ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Select a template to get started</p>
                </div>
              ) : (
                <div>
                  <textarea
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      setCustomMode(true);
                    }}
                    placeholder="Type your message here..."
                    className="w-full h-80 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                    disabled={!customMode && selectedTemplate !== 'custom'}
                  />

                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-sm text-gray-500">{message.length} characters</p>
                    <button
                      onClick={() => copyToClipboard(message)}
                      className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                    >
                      {copied ? (
                        <>
                          <CheckCircle size={16} className="mr-1 text-green-600" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={16} className="mr-1" />
                          Copy Message
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            {message && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center">
                  <MessageCircle size={20} className="mr-2 text-purple-600" />
                  Message Preview
                </h3>

                <div className="bg-white rounded-2xl rounded-tl-none shadow-lg p-6 max-w-md relative">
                  <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-white border-r-[12px] border-r-transparent"></div>
                  <div className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
                    {message}
                  </div>
                  <div className="text-xs text-gray-400 mt-3 text-right">
                    {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            )}

            {/* Action Button */}
            {message && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <button
                  onClick={sendToWhatsApp}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center group"
                >
                  <MessageCircle size={24} className="mr-3 group-hover:scale-110 transition-transform" />
                  {copied ? (
                    <>
                      <CheckCircle size={20} className="mr-2" />
                      Copied! Opening WhatsApp...
                    </>
                  ) : (
                    <>
                      <Copy size={20} className="mr-2" />
                      Copy & Open WhatsApp
                    </>
                  )}
                </button>

                <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 text-sm mb-2">📱 How it works:</h4>
                  <ol className="text-xs text-purple-800 space-y-1 list-decimal list-inside">
                    <li>Click the button to copy the message and open WhatsApp</li>
                    <li>Paste the message in your WhatsApp group chat</li>
                    <li>Review and click Send in WhatsApp</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventWhatsAppMessenger;